import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import App from "./app/app";
import { ErrorBoundary } from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { AnalyticsProvider } from "@/zapi-frontend/app/providers/analytics";
import { initChunkLoadErrorHandler } from "@/zapi-frontend/shared/lib/chunkLoadErrorHandler";
import { IS_PROTO } from "@/zapi-frontend/proto/infra/protoMode";
import { installProto1 } from "@/zapi-frontend/proto/infra/install";
import "@/zapi-frontend/shared/i18n";

initChunkLoadErrorHandler();

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
const queryClient = new QueryClient();

function ToastLoadingIcon() {
  const { t } = useTranslation();
  return <Spinner aria-label={t("loading")} className="size-5 text-muted-foreground" />;
}

const start = async () => {
  if (IS_PROTO) await installProto1();
  renderApp();
};

const renderApp = () =>
  root.render(
  <StrictMode>
    <AnalyticsProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ErrorBoundary
            fallback={<div>Error</div>}
            onError={IS_PROTO ? (error) => console.error("[proto1] render failed:", error) : undefined}
            beforeCapture={(scope) => {
              scope.setTag("location", "app");
            }}
          >
            <App />
            <Toaster
              position="top-right"
              // sonner hardcodes its own font stack on the toaster; inherit the app's Inter instead
              style={{ fontFamily: "inherit" }}
              icons={{ loading: <ToastLoadingIcon /> }}
              toastOptions={{ classNames: { title: "text-[14px] leading-[13px] font-medium" } }}
            />
          </ErrorBoundary>
        </BrowserRouter>
      </QueryClientProvider>
    </AnalyticsProvider>
  </StrictMode>
  );

void start();
